import React, { useState, useEffect } from 'react';
import ExportButtons from './ExportButtons';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement, PointElement, LinearScale, CategoryScale,
  Title, Tooltip, Legend, ArcElement, BarElement, Filler
} from 'chart.js';

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip, Legend, ArcElement, BarElement, Filler);

const tooltipStyle = {
  backgroundColor: 'rgba(18,18,30,0.95)',
  borderColor: 'rgba(255,255,255,0.1)',
  borderWidth: 1,
  padding: 12,
  cornerRadius: 8,
  titleColor: '#fff',
  bodyColor: 'rgba(255,255,255,0.8)',
};

const gridStyle = { color: 'rgba(255,255,255,0.04)' };
const tickStyle = { color: 'rgba(255,255,255,0.4)', font: { size: 11 } };
const noGrid = { display: false };
const noBorder = { display: false };

const VideoPipeline = () => {
  const [timeRange, setTimeRange] = useState('24h');

  const kpiMetrics = [
    { label: 'Transcoding Queue', value: '23', change: '+4', positive: true, icon: 'fas fa-tasks', color: '#f82581' },
    { label: 'CDN Hit Rate', value: '98.7%', change: '+0.3%', positive: true, icon: 'fas fa-bolt', color: '#10b981' },
    { label: 'Avg Processing Time', value: '45s/min', change: '-2s', positive: true, icon: 'fas fa-hourglass-end', color: '#6366f1' },
    { label: 'Active Edge Nodes', value: '142', change: '+8', positive: true, icon: 'fas fa-globe', color: '#f59e0b' },
  ];

  const transcodingJobs = [
    { id: 'TRX-2847', series: 'The Crown S3', episode: 'Ep 04', resolution: '4K', progress: 85, status: 'processing', eta: '8m' },
    { id: 'TRX-2846', series: 'Bridgerton S2', episode: 'Ep 06', resolution: '1080p', progress: 100, status: 'complete', eta: '—' },
    { id: 'TRX-2845', series: 'Stranger Things S4', episode: 'Ep 09', resolution: '720p', progress: 45, status: 'processing', eta: '12m' },
    { id: 'TRX-2844', series: 'The Witcher S3', episode: 'Ep 02', resolution: '4K', progress: 0, status: 'queued', eta: '25m' },
    { id: 'TRX-2843', series: 'Ozark S4', episode: 'Ep 13', resolution: '1080p', progress: 100, status: 'complete', eta: '—' },
    { id: 'TRX-2842', series: 'Dark S1', episode: 'Ep 08', resolution: '480p', progress: 0, status: 'failed', eta: 'Retry' },
  ];

  const cdnRegions = [
    { region: 'NA (North America)', nodes: 45, latency: '32ms', hitRate: '99.2%', bandwidth: '342Gbps', status: 'healthy' },
    { region: 'EU (Europe)', nodes: 38, latency: '28ms', hitRate: '98.8%', bandwidth: '287Gbps', status: 'healthy' },
    { region: 'Asia-Pacific', nodes: 52, latency: '45ms', hitRate: '97.5%', bandwidth: '456Gbps', status: 'healthy' },
    { region: 'LATAM', nodes: 18, latency: '58ms', hitRate: '96.2%', bandwidth: '128Gbps', status: 'warning' },
    { region: 'Middle East', nodes: 12, latency: '42ms', hitRate: '98.1%', bandwidth: '89Gbps', status: 'healthy' },
    { region: 'Africa', nodes: 8, latency: '72ms', hitRate: '94.5%', bandwidth: '32Gbps', status: 'warning' },
  ];

  const drmSecurityStatus = [
    { label: 'Widevine', status: 'Active', version: 'v4.2', success: '99.9%', color: '#10b981' },
    { label: 'FairPlay', status: 'Active', version: 'v3.1', success: '99.8%', color: '#6366f1' },
    { label: 'Signed URLs', status: 'Enabled', version: '15min expiry', success: 'Yes', color: '#f59e0b' },
    { label: 'Encryption at Rest', status: 'AES-256', version: 'Active', success: 'Yes', color: '#f82581' },
  ];

  const chartOpts = (yCallback) => ({
    responsive: true, maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { display: false }, tooltip: tooltipStyle },
    scales: {
      x: { ticks: tickStyle, grid: noGrid, border: noBorder },
      y: { ticks: { ...tickStyle, callback: yCallback }, grid: gridStyle, border: noBorder },
    },
  });

  return (
    <div className="vp-container">
      <div className="vp-header">
        <div className="vp-header-left">
          <h4>Video Pipeline & CDN</h4>
          <p className="vp-subtitle">Transcoding, delivery & content pipeline monitoring</p>
        </div>
        <div className="vp-header-right" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <ExportButtons
            data={[
              { id: 'TRX-2847', series: 'The Crown S3', episode: 'Ep 04', resolution: '4K', progress: '85%', status: 'Processing', eta: '8m' },
              { id: 'TRX-2846', series: 'Bridgerton S2', episode: 'Ep 06', resolution: '1080p', progress: '100%', status: 'Complete', eta: '—' },
              { id: 'TRX-2845', series: 'Stranger Things S4', episode: 'Ep 09', resolution: '720p', progress: '45%', status: 'Processing', eta: '12m' },
              { id: 'TRX-2844', series: 'The Witcher S3', episode: 'Ep 02', resolution: '4K', progress: '0%', status: 'Queued', eta: '25m' },
              { id: 'TRX-2843', series: 'Ozark S4', episode: 'Ep 13', resolution: '1080p', progress: '100%', status: 'Complete', eta: '—' },
              { id: 'TRX-2842', series: 'Dark S1', episode: 'Ep 08', resolution: '480p', progress: '0%', status: 'Failed', eta: 'Retry' },
            ]}
            headers={[
              { key: 'id', label: 'Job ID' }, { key: 'series', label: 'Series' }, { key: 'episode', label: 'Episode' },
              { key: 'resolution', label: 'Resolution' }, { key: 'progress', label: 'Progress' }, { key: 'status', label: 'Status' }, { key: 'eta', label: 'ETA' },
            ]}
            title="Video Pipeline & CDN Report"
            filename="video_pipeline"
          />
          <div className="vp-time-range">
            {['24h', '7d', '30d'].map(r => (
              <button key={r} className={`vp-range-btn ${timeRange === r ? 'active' : ''}`} onClick={() => setTimeRange(r)}>
                {r}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="vp-metrics-grid">
        {kpiMetrics.map((m, i) => (
          <div key={i} className="vp-metric-card" style={{ animationDelay: `${i * 0.06}s` }}>
            <div className="vp-metric-top">
              <div className="vp-metric-icon" style={{ background: m.color + '15', color: m.color }}><i className={m.icon}></i></div>
              <span className={`vp-metric-change ${m.positive ? 'up' : 'down'}`}><i className={`fas fa-arrow-${m.positive ? 'up' : 'down'}`}></i> {m.change}</span>
            </div>
            <div className="vp-metric-value">{m.value}</div>
            <div className="vp-metric-label">{m.label}</div>
          </div>
        ))}
      </div>

      <div className="vp-chart-card">
        <div className="vp-chart-head">
          <h6><i className="fas fa-list"></i> Transcoding Queue</h6>
          <span className="vp-badge-sm">{transcodingJobs.length} Jobs</span>
        </div>
        <div className="vp-table-wrap">
          <table className="vp-table">
            <thead>
              <tr>
                <th>Job ID</th>
                <th>Series</th>
                <th>Episode</th>
                <th>Resolution</th>
                <th>Progress</th>
                <th>Status</th>
                <th>ETA</th>
              </tr>
            </thead>
            <tbody>
              {transcodingJobs.map((j, i) => (
                <tr key={i}>
                  <td><code className="vp-job-id">{j.id}</code></td>
                  <td className="vp-series-name">{j.series}</td>
                  <td>{j.episode}</td>
                  <td><span className="vp-resolution-badge">{j.resolution}</span></td>
                  <td>
                    <div className="vp-progress-wrap">
                      <div className="vp-progress-bar" style={{ width: `${j.progress}%` }}></div>
                      <span className="vp-progress-text">{j.progress}%</span>
                    </div>
                  </td>
                  <td><span className={`vp-status-badge ${j.status}`}>{j.status}</span></td>
                  <td className="vp-eta">{j.eta}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="vp-row-2col">
        <div className="vp-chart-card">
          <div className="vp-chart-head">
            <h6><i className="fas fa-server"></i> Bitrate Ladder</h6>
          </div>
          <div className="vp-chart-body" style={{ height: 240 }}>
            <Bar data={{
              labels: ['360p', '480p', '720p', '1080p', '1440p'],
              datasets: [{
                label: 'Bitrate (kbps)',
                data: [800, 1500, 3000, 5000, 8000],
                backgroundColor: ['rgba(16,185,129,0.6)', 'rgba(99,102,241,0.6)', 'rgba(248,37,129,0.6)', 'rgba(245,158,11,0.6)', 'rgba(248,37,129,0.7)'],
                borderRadius: 6,
                borderSkipped: false,
                barPercentage: 0.7,
              }],
            }} options={{
              responsive: true, maintainAspectRatio: false, indexAxis: 'y',
              plugins: { legend: { display: false }, tooltip: { ...tooltipStyle, callbacks: { label: ctx => `${ctx.parsed.x}kbps` } } },
              scales: {
                x: { ticks: { ...tickStyle, callback: v => v + 'k' }, grid: gridStyle, border: noBorder },
                y: { ticks: tickStyle, grid: noGrid, border: noBorder },
              },
            }} />
          </div>
        </div>

        <div className="vp-chart-card">
          <div className="vp-chart-head">
            <h6><i className="fas fa-chart-line"></i> CDN Performance (24h)</h6>
            <div className="vp-chart-legend-inline">
              <span><span className="vp-dot" style={{ background: '#10b981' }}></span> Cache Hit %</span>
              <span><span className="vp-dot" style={{ background: '#f82581' }}></span> Bandwidth Gbps</span>
            </div>
          </div>
          <div className="vp-chart-body" style={{ height: 240 }}>
            <Line data={{
              labels: ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '23:59'],
              datasets: [
                { label: 'Cache Hit Rate %', data: [97.2, 96.8, 97.5, 98.1, 98.4, 98.7, 98.5], borderColor: '#10b981', backgroundColor: 'rgba(16,185,129,0.08)', tension: 0.4, fill: true, pointRadius: 0, pointHoverRadius: 5, borderWidth: 2.5, yAxisID: 'y' },
                { label: 'Bandwidth (Gbps)', data: [245, 198, 312, 456, 523, 487, 398], borderColor: '#f82581', backgroundColor: 'rgba(248,37,129,0.08)', tension: 0.4, fill: true, pointRadius: 0, pointHoverRadius: 5, borderWidth: 2.5, yAxisID: 'y1' },
              ],
            }} options={{
              responsive: true, maintainAspectRatio: false,
              interaction: { mode: 'index', intersect: false },
              plugins: { legend: { display: false }, tooltip: tooltipStyle },
              scales: {
                x: { ticks: tickStyle, grid: noGrid, border: noBorder },
                y: { type: 'linear', position: 'left', ticks: { ...tickStyle, callback: v => v + '%' }, grid: gridStyle, border: noBorder },
                y1: { type: 'linear', position: 'right', ticks: { ...tickStyle, callback: v => v + 'G' }, grid: noGrid, border: noBorder },
              },
            }} />
          </div>
        </div>
      </div>

      <div className="vp-chart-card">
        <div className="vp-chart-head">
          <h6><i className="fas fa-map"></i> CDN Regional Performance</h6>
        </div>
        <div className="vp-table-wrap">
          <table className="vp-table">
            <thead>
              <tr>
                <th>Region</th>
                <th>Edge Nodes</th>
                <th>Avg Latency</th>
                <th>Cache Hit Rate</th>
                <th>Bandwidth</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {cdnRegions.map((r, i) => (
                <tr key={i}>
                  <td className="vp-region-name">{r.region}</td>
                  <td><strong>{r.nodes}</strong></td>
                  <td>{r.latency}</td>
                  <td><span className="vp-hit-rate">{r.hitRate}</span></td>
                  <td>{r.bandwidth}</td>
                  <td><span className={`vp-region-status ${r.status}`}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="vp-row-2col">
        <div className="vp-chart-card">
          <div className="vp-chart-head">
            <h6><i className="fas fa-broadcast-tower"></i> Content Delivery Stats</h6>
          </div>
          <div className="vp-chart-body vp-doughnut-wrap" style={{ height: 200 }}>
            <Doughnut data={{
              labels: ['HLS', 'DASH', 'Progressive'],
              datasets: [{
                data: [72, 18, 10],
                backgroundColor: ['#f82581', '#6366f1', '#10b981'],
                borderWidth: 0,
                cutout: '65%',
                spacing: 2,
              }],
            }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { ...tooltipStyle, callbacks: { label: ctx => `${ctx.label}: ${ctx.parsed}%` } } } }} />
          </div>
          <div className="vp-protocol-labels">
            {['HLS', 'DASH', 'Progressive'].map((p, i) => (
              <span key={i}><span className="vp-dot" style={{ background: ['#f82581', '#6366f1', '#10b981'][i] }}></span> {p} {[72, 18, 10][i]}%</span>
            ))}
          </div>
        </div>

        <div className="vp-chart-card">
          <div className="vp-chart-head">
            <h6><i className="fas fa-shield-alt"></i> DRM & Security Status</h6>
          </div>
          <div className="vp-drm-list">
            {drmSecurityStatus.map((d, i) => (
              <div key={i} className="vp-drm-item">
                <div className="vp-drm-header">
                  <div className="vp-drm-icon" style={{ background: d.color + '15', color: d.color }}>
                    <i className={d.label === 'Widevine' ? 'fas fa-key' : d.label === 'FairPlay' ? 'fas fa-apple' : d.label === 'Signed URLs' ? 'fas fa-link' : 'fas fa-lock'}></i>
                  </div>
                  <div className="vp-drm-info">
                    <span className="vp-drm-label">{d.label}</span>
                    <span className="vp-drm-version">{d.version}</span>
                  </div>
                  <div className="vp-drm-status">
                    <span className="vp-drm-status-badge" style={{ background: d.color + '15', color: d.color }}>{d.status}</span>
                    <span className="vp-drm-success">{d.success}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPipeline;
